/**
 * OMNISE UTM ATTRIBUTION + CTA LINK REWRITER
 * ---------------------------------------------------------------
 * 1) Attribution (utm_source/utm_campaign/utm_content) được lưu trong cookie.
   - Nếu URL có UTM -> lưu/ghi đè theo UTM trên URL.
   - Nếu chưa từng có attribution -> lưu giá trị mặc định.
   - Dữ liệu tồn tại 30 ngày.

 * 2) Có 2 kiểu link CTA được xử lý:

        a) Link đã set cứng sẵn dạng https://omnise.app/go?utm_content=...&utm_campaign=...&utm_source=...
           -> JS chỉ ghi đè 2 tham số utm_campaign & utm_source bằng giá trị lấy từ cookie || default
              utm_content (và các tham số khác như template_id) giữ nguyên.
    
        b) Link dạng attribute, href chỉ là trang thường (vd href="https://omnise.app/"), kèm attribute:
              <a href="https://omnise.app/" data-cta="Start free now" data-utm="">
           -> JS sẽ tự build lại toàn bộ href thành:
              https://omnise.app/go?utm_content=start_free_now&utm_campaign=...&utm_source=...
              utm_content: lấy từ data-cta,
              Nếu không có data-cta thì utm_content mặc định = "omnise".
 
 * 3) Các Domain trong AFFILIATE_HOSTS => Sẽ bỏ qua 
 
 * 4) PAGE_CAMPAIGNS: URL path nào match thì sẽ dùng campaign tương ứng
    
*/

const OmniseUTM = {

    config: {
        PAGE_CAMPAIGNS: [
            {
                match: '/pages/templates',
                campaign: 'template',
            },
            {
                match: '/pages/hypothesis',
                campaign: 'template',
            },
        ],

        AFFILIATE_HOSTS: [
            'shopify.pxf.io',
            'pxf.io'
        ],

        CLICK_ID_SOURCES: {
            fbclid: 'facebook',
            gclid: 'google',
            msclkid: 'bing',
            ttclid: 'tiktok',
            twclid: 'twitter',
        },

        UTM_KEYS: ['utm_source', 'utm_campaign', 'utm_content'],

        DEFAULT_UTM: {
            utm_campaign: 'homepage',
            utm_source: 'omnise.com',
        },

        STORAGE_KEY: 'omnise_utm',
        STORAGE_DAYS: 30,

        GO_HOST: 'omnise.app',
        GO_PATH: '/go'
    },

    init() {
        const { store, links } = this;

        // 1) Nếu URL hiện tại có utm -> lưu/ghi đè cookie 30 ngày
        store.captureUTMFromURL();

        // 2) Rewrite toàn bộ link CTA lần đầu load trang
        links.updateCTALinks(store.getAttributionParams());

        // 3) Theo dõi link CTA được render/chèn vào DOM bởi Js
        let debounceTimer = null;
        const observer = new MutationObserver(() => {
            clearTimeout(debounceTimer);

            debounceTimer = setTimeout(() => {
                links.updateCTALinks(
                    store.getAttributionParams()
                );
            }, 150);
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    },

    store: {
        saveUTM(newData) {
            const { helpers, config } = OmniseUTM;
            const now = Date.now();
            const existing = helpers.getStorage(config.STORAGE_KEY);

            const record = {
                value: { ...(existing && existing.value), ...newData },
                created_at: (existing && existing.created_at) || now,
                updated_at: now,
            };

            helpers.setStorage(config.STORAGE_KEY, record, config.STORAGE_DAYS);
            return record;
        },

        getStoredUTMValue() {
            const { helpers, config } = OmniseUTM;
            return helpers.getStorage(config.STORAGE_KEY)?.value || null;
        },

        captureUTMFromURL() {
            const { store, helpers, config } = OmniseUTM;

            const urlUTM = helpers.getUTMFromURL();
            const stored = store.getStoredUTMValue();

            // Có UTM trên URL -> luôn ghi đè
            if (Object.keys(urlUTM).length > 0) {
                store.saveUTM({
                    ...urlUTM,
                    attribution_type: 'url',
                });
                return;
            }

            // Chưa từng lưu -> lưu attribution mặc định
            if (!stored) {
                store.saveUTM({
                    ...config.DEFAULT_UTM,
                    utm_campaign: helpers.getDefaultCampaign(),
                    attribution_type: 'default',
                });

            }
        },

        getAttributionParams() {
            const { config, store, helpers } = OmniseUTM;
            const stored = store.getStoredUTMValue();

            return {
                utm_campaign: helpers.shouldUseStoredCampaign(stored)
                    ? stored.utm_campaign
                    : helpers.getDefaultCampaign(),

                utm_source: stored?.utm_source || config.DEFAULT_UTM.utm_source,
            };
        },
    },

    links: {
        getUTMContent(link) {
            const content = link.dataset.cta;
            return content && content.trim() ? OmniseUTM.helpers.normalize(content) : 'omnise';
        },

        buildGoURL(attribution, content) {
            const { GO_HOST, GO_PATH } = OmniseUTM.config;
            const params = new URLSearchParams();
            params.set('utm_content', content);
            params.set('utm_campaign', attribution.utm_campaign);
            params.set('utm_source', attribution.utm_source);
            return `https://${GO_HOST}${GO_PATH}?${params.toString()}`;
        },

        updateCTALinks(attribution) {
            const { GO_HOST, GO_PATH, AFFILIATE_HOSTS } = OmniseUTM.config;
            const { getUTMContent, buildGoURL } = OmniseUTM.links;


            /** 1. <a data-utm data-cta="..."> -> tự build href đầy đủ */
            document.querySelectorAll('a[data-utm]').forEach((link) => {

                const href = link.getAttribute('href');
                if (!href) return;

                try {
                    const { hostname } = new URL(href, window.location.origin);
                    const normalizedHost = hostname.toLowerCase();

                    const isAffiliate = AFFILIATE_HOSTS.some(
                        host => normalizedHost === host || normalizedHost.endsWith(`.${host}`)
                    );

                    if (isAffiliate) {
                        return;
                    }
                } catch (_) { }

                const content = getUTMContent(link);
                const newHref = buildGoURL(attribution, content);

                if (href !== newHref) {
                    link.setAttribute('href', newHref);
                }

            });

            /** 2. <a href="https://omnise.app/go?utm_content=...&template_id=..."> 
                -> chỉ ghi đè utm_campaign & utm_source, giữ nguyên phần còn lại
            */
            document
                .querySelectorAll(`a[href*="${GO_HOST}${GO_PATH}"]:not([data-utm])`)
                .forEach((link) => {
                    const rawHref = link.getAttribute('href');
                    if (!rawHref) return;

                    try {
                        const urlObj = new URL(rawHref, window.location.origin);

                        if (urlObj.hostname.toLowerCase() !== GO_HOST || urlObj.pathname !== GO_PATH) return;

                        urlObj.searchParams.set('utm_campaign', attribution.utm_campaign);
                        urlObj.searchParams.set('utm_source', attribution.utm_source);

                        const newHref = urlObj.toString();

                        if (rawHref !== newHref) {
                            link.setAttribute('href', newHref);
                        }
                    } catch (err) {
                        console.error('CTA URL update error:', err, link);
                    }
                });
        },
    },

    helpers: {

        setStorage(key, valueObj, days) {
            const record = {
                ...valueObj,
                expiredAt: Date.now() + days * 24 * 60 * 60 * 1000,
            };

            try {
                const cookieValue = encodeURIComponent(
                    JSON.stringify(record)
                );

                document.cookie =
                    `${key}=${cookieValue}; ` +
                    `max-age=${days * 24 * 60 * 60}; ` +
                    `path=/; ` +
                    `SameSite=Lax; ` +
                    `Secure`;

            } catch (e) {
                console.error('OmniseUTM: cookie set failed', e);
            }
        },

        getStorage(key) {
            try {
                const cookies = document.cookie
                    .split(';')
                    .reduce((acc, item) => {
                        const [cookieKey, ...rest] = item.trim().split('=');

                        if (cookieKey) {
                            acc[cookieKey] = rest.join('=');
                        }

                        return acc;
                    }, {});

                const raw = cookies[key];

                if (!raw) return null;

                const record = JSON.parse(
                    decodeURIComponent(raw)
                );

                if (record.expiredAt && Date.now() > record.expiredAt) {
                    this.removeCookie(key);
                    return null;
                }

                return record;

            } catch (e) {
                this.removeCookie(key);
                return null;
            }
        },

        removeCookie(key) {
            document.cookie =
                `${key}=;` +
                `max-age=0;` +
                `path=/;` +
                `SameSite=Lax;` +
                `Secure`;
        },

        normalize(str) {
            return (str || '')
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_|_$/g, '');
        },

        getUTMFromURL() {
            const { UTM_KEYS, CLICK_ID_SOURCES } = OmniseUTM.config;

            const params = new URLSearchParams(window.location.search);
            const utm = {};

            UTM_KEYS.forEach((key) => {
                const value = params.get(key);
                if (value) {
                    utm[key] = value;
                }
            });

            if (!utm.utm_source) {
                for (const [clickId, source] of Object.entries(CLICK_ID_SOURCES)) {
                    if (params.has(clickId)) {
                        utm.utm_source = source;
                        break;
                    }
                }
            }

            return utm;
        },

        getDefaultCampaign() {
            const { PAGE_CAMPAIGNS, DEFAULT_UTM } = OmniseUTM.config;
            const pathname = window.location.pathname;

            const page = PAGE_CAMPAIGNS.find(item =>
                pathname === item.match ||
                pathname.startsWith(item.match + '/')
            );

            return page?.campaign || this.getPageTypeCampaign() || DEFAULT_UTM.utm_campaign;
        },

        getPageTypeCampaign() {
            const pageType =
                window.OmnisePageContext?.page_type ||
                document.body?.dataset.pageType ||
                '';
            const campaign = this.normalize(pageType);

            if (campaign === 'page') {
                return this.getPageHandleCampaign() || campaign;
            }

            return campaign === 'index' ? 'homepage' : campaign;
        },

        getPageHandleCampaign() {
            const pageHandle =
                window.OmnisePageContext?.page_handle ||
                document.body?.dataset.pageHandle ||
                window.location.pathname.replace(/^\/+|\/+$/g, '').split('/').pop() ||
                '';

            return this.normalize(pageHandle);
        },

        shouldUseStoredCampaign(stored) {
            const { DEFAULT_UTM } = OmniseUTM.config;

            if (!stored?.utm_campaign) {
                return false;
            }

            if (stored.attribution_type === 'url') {
                return true;
            }

            if (stored.attribution_type === 'default') {
                return false;
            }

            return stored.utm_campaign !== DEFAULT_UTM.utm_campaign;
        }
    }

};

window.addEventListener('load', () => {
    OmniseUTM.init();
});